import React, { useEffect, useRef } from "react";
import {
  PlayIcon,
  PauseIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { useGlobalStopwatch } from "./StopwatchContext";

interface StickyStopwatchProps {
  position: number;
  onHeightChange: (height: number) => void;
}

const StickyStopwatch: React.FC<StickyStopwatchProps> = ({
  position,
  onHeightChange,
}) => {
  const { elapsedTime, start, pause, reset, isPaused } = useGlobalStopwatch();

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      onHeightChange(containerRef.current.offsetHeight);
    }
  }, [onHeightChange]);

  const minutes = Math.floor(elapsedTime / 60000);
  const seconds = Math.floor((elapsedTime % 60000) / 1000);
  const milliseconds = Math.floor((elapsedTime % 1000) / 10);

  return (
    <div
      ref={containerRef}
      style={{ bottom: position }}
      className="p-2 absolute w-full bg-indigo-100 flex flex-row gap-x-1 justify-between align-middle items-center"
    >
      <p className="text-sm font-medium text-gray-700">Stopwatch</p>
      <div className="grid grid-cols-[45px,auto,45px,auto,45px] items-center gap-y-[1px]">
        <div className="flex justify-center">
          <p className="text-3xl leading-none	line font-extralight text-gray-700">
            {minutes.toString().padStart(2, "0")}
          </p>
        </div>

        <p className="text-3xl leading-none font-extralight text-gray-700">:</p>

        <div className="flex justify-center">
          <p className="text-3xl leading-none font-extralight text-gray-700">
            {seconds.toString().padStart(2, "0")}
          </p>
        </div>

        <p className="text-3xl leading-none font-extralight text-gray-700">:</p>

        <div className="flex justify-center">
          <p className="text-3xl leading-none font-extralight text-gray-700">
            {milliseconds.toString().padStart(2, "0")}
          </p>
        </div>

        <div className="flex justify-center">
          <p className="text-xs leading-none font-light text-gray-700">min</p>
        </div>
        <div></div>
        <div className="flex justify-center">
          <p className="text-xs leading-none font-light text-gray-700">sec</p>
        </div>
        <div></div>
        <div className="flex justify-center">
          <p className="text-xs leading-none font-light text-gray-700">ms</p>
        </div>
      </div>
      <div className="flex flex-row gap-x-1">
        {isPaused && (
          <button
            className="flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 pl-[11px] pr-[9px] text-sm font-medium text-white h-fit"
            onClick={start}
          >
            <PlayIcon className="h-5 w-5 text-white" aria-hidden="true" />
          </button>
        )}
        {!isPaused && (
          <button
            className="flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 pl-[11px] pr-[9px] text-sm font-medium text-white h-fit"
            onClick={pause}
          >
            <PauseIcon className="h-5 w-5 text-white" aria-hidden="true" />
          </button>
        )}

        <button
          className="flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-2.5 text-sm font-medium text-white h-fit"
          onClick={reset}
        >
          <ArrowPathIcon className="h-5 w-5 text-white" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default StickyStopwatch;
